// Sentinel productId used for the placeholders that stand in for a null
// (Generic) combination slot. Downstream code (SystemMap, quote-column,
// tax/shipping APIs) checks for this value to distinguish placeholders from
// real purchasable products.
export const KEPT_EQUIPMENT_PRODUCT_ID = 0;

export const KEPT_EQUIPMENT_NOTE = 'Keeping existing equipment';

// The indoor slot of a packaged system holds no equipment of its own: the
// coil and blower are built into the unit the outdoor slot sells. The
// placeholder that stands in for it carries the unit's capacities, unlike a
// kept one, so the zone table can size the system.
export const PACKAGED_UNIT_SECTION_NAME = 'Packaged Unit';
export const PACKAGED_UNIT_SECTION_NOTE = 'Coil and blower built in';

const REQUIRED_NAME_SUFFIX = ' (Required)';

export type EquipmentPlaceholder =
  | { role: 'kept' }
  | { role: 'required'; label: string }
  | { role: 'packaged' };

export type PlaceholderNoteFields = { placeholderNote?: string | null };

type PlaceholderProduct = PlaceholderNoteFields & {
  productId: number;
  name: string;
};

export function isEquipmentPlaceholderProduct(
  product: { productId: number } | null | undefined
): boolean {
  return product?.productId === KEPT_EQUIPMENT_PRODUCT_ID;
}

export function equipmentPlaceholderName(
  placeholder: EquipmentPlaceholder,
  label: string
): string {
  if (placeholder.role === 'required') {
    return `${label}${REQUIRED_NAME_SUFFIX}`;
  }
  if (placeholder.role === 'packaged') {
    return PACKAGED_UNIT_SECTION_NAME;
  }
  return `Existing ${label} (Keeping)`;
}

export function equipmentPlaceholderDescription(
  placeholder: EquipmentPlaceholder,
  label: string
): string {
  if (placeholder.role === 'required') {
    return `Requires ${label.toLowerCase()}`;
  }
  if (placeholder.role === 'packaged') {
    return `Coil and blower are built into the ${label.toLowerCase()} unit`;
  }
  return `Keeping existing ${label.toLowerCase()}`;
}

export function equipmentPlaceholderNoteText(
  placeholder: EquipmentPlaceholder
): string {
  if (placeholder.role === 'required') {
    return `Requires ${placeholder.label}`;
  }
  if (placeholder.role === 'packaged') {
    return PACKAGED_UNIT_SECTION_NOTE;
  }
  return KEPT_EQUIPMENT_NOTE;
}

function legacyEquipmentPlaceholderNote(name: string): string {
  if (name === PACKAGED_UNIT_SECTION_NAME) {
    return PACKAGED_UNIT_SECTION_NOTE;
  }
  const label = name.endsWith(REQUIRED_NAME_SUFFIX)
    ? name.slice(0, -REQUIRED_NAME_SUFFIX.length)
    : '';
  return label.length > 0 ? `Requires ${label}` : KEPT_EQUIPMENT_NOTE;
}

export function equipmentPlaceholderNote(
  product: PlaceholderProduct | null | undefined
): string | null {
  if (!product || !isEquipmentPlaceholderProduct(product)) return null;
  return (
    product.placeholderNote ?? legacyEquipmentPlaceholderNote(product.name)
  );
}

// The packaged-unit section, as opposed to a kept or required placeholder:
// the one placeholder whose capacities are known, because they are the sold
// unit's own.
export function isPackagedUnitSectionProduct(
  product: PlaceholderProduct | null | undefined
): boolean {
  return equipmentPlaceholderNote(product) === PACKAGED_UNIT_SECTION_NOTE;
}
